import React, { lazy } from 'react';
import { ArrowRightIcon, PackageCheckIcon } from 'lucide-react';
import { Link } from 'react-router-dom';
import { Locale, useLocale } from '../components/LocaleProvider';
import { SiteLayout } from '../components/SiteLayout';
import { useScreenInit } from '../useScreenInit';
type Copy = Record<Locale, string>;
type Category = {
  name: Copy;
  detail: Copy;
  image: string;
  to: string;
};
type Product = {
  name: Copy;
  type: Copy;
  brand: string;
  image: string;
};
const categories: Category[] = [{
  name: {
    fr: 'Marché frais',
    en: 'Fresh market'
  },
  detail: {
    fr: 'Fruits · Légumes · Boucherie',
    en: 'Fruit · Vegetables · Meat'
  },
  image: 'https://images.unsplash.com/photo-1542838132-92c53300491e?auto=format&fit=crop&w=800&q=85',
  to: '/categories#fresh-fruits-vegetables'
}, {
  name: {
    fr: 'Épicerie',
    en: 'Food & pantry'
  },
  detail: {
    fr: 'Riz · Huiles · Conserves',
    en: 'Rice · Oil · Canned food'
  },
  image: 'https://images.unsplash.com/photo-1601050690597-df0568f70950?auto=format&fit=crop&w=1000&q=90',
  to: '/categories#food-and-pantry'
}, {
  name: {
    fr: 'Boissons, vins & spiritueux',
    en: 'Beverages, wine & spirits'
  },
  detail: {
    fr: 'Eaux · Sodas · Café · Thé · Vin',
    en: 'Water · Soft drinks · Coffee · Tea · Wine'
  },
  image: 'https://images.unsplash.com/photo-1534353473418-4cfa6c56fd38?auto=format&fit=crop&w=1000&q=90',
  to: '/categories#beverages-wine-and-spirits'
}, {
  name: {
    fr: 'Santé & parapharmacie',
    en: 'Health & personal care'
  },
  detail: {
    fr: 'Santé · Beauté · Bébé',
    en: 'Medical · Beauty · Baby'
  },
  image: 'https://images.unsplash.com/photo-1584515933487-779824d29309?auto=format&fit=crop&w=800&q=85',
  to: '/categories#health-and-medical'
}, {
  name: {
    fr: 'Électroménager & appareils',
    en: 'Electricals & appliances'
  },
  detail: {
    fr: 'TV · Appareils · Mobile',
    en: 'TVs · Appliances · Mobile'
  },
  image: 'https://images.unsplash.com/photo-1558002038-1055907df827?auto=format&fit=crop&w=800&q=85',
  to: '/categories#electricals-and-appliances'
}, {
  name: {
    fr: 'Maison & linge',
    en: 'Home & textiles'
  },
  detail: {
    fr: 'Draps · Oreillers · Décoration',
    en: 'Bedding · Pillows · Décor'
  },
  image: 'https://images.unsplash.com/photo-1616486338812-3dadae4b4ace?auto=format&fit=crop&w=800&q=85',
  to: '/categories#bedding-and-home-textiles'
}];
const products: Product[] = [{
  name: {
    fr: 'Café intense sélection',
    en: 'Signature blend coffee'
  },
  type: {
    fr: 'Boissons',
    en: 'Beverages'
  },
  brand: 'Nescafé',
  image: 'https://images.unsplash.com/photo-1495474472287-4d71bcdd2085?auto=format&fit=crop&w=800&q=85'
}, {
  name: {
    fr: 'Huile d’olive vierge extra',
    en: 'Extra virgin olive oil'
  },
  type: {
    fr: 'Épicerie',
    en: 'Food & pantry'
  },
  brand: 'Terra Verde',
  image: 'https://images.unsplash.com/photo-1474979266404-7eaacbcd87c5?auto=format&fit=crop&w=800&q=85'
}, {
  name: {
    fr: 'Eau minérale pétillante',
    en: 'Sparkling mineral water'
  },
  type: {
    fr: 'Eaux minérales',
    en: 'Mineral water'
  },
  brand: 'AquaPure',
  image: 'https://images.unsplash.com/photo-1550505095-81378a674395?auto=format&fit=crop&w=800&q=85'
}, {
  name: {
    fr: 'Collection de vins rouges',
    en: 'Reserve red wine collection'
  },
  type: {
    fr: 'Vins',
    en: 'Wine'
  },
  brand: 'Charentais Cellars',
  image: 'https://images.unsplash.com/photo-1510812431401-41d2bd2722f3?auto=format&fit=crop&w=800&q=85'
}, {
  name: {
    fr: 'Linge de lit en coton',
    en: 'Soft cotton bedding set'
  },
  type: {
    fr: 'Linge de maison',
    en: 'Home textiles'
  },
  brand: 'Home Comfort',
  image: 'https://images.unsplash.com/photo-1616486338812-3dadae4b4ace?auto=format&fit=crop&w=800&q=85'
}, {
  name: {
    fr: 'Kit d’outillage sans fil',
    en: 'Cordless power tool kit'
  },
  type: {
    fr: 'Bricolage',
    en: 'DIY & hardware'
  },
  brand: 'BuildPro',
  image: 'https://images.unsplash.com/photo-1504148455328-c376907d081c?auto=format&fit=crop&w=800&q=85'
}, {
  name: {
    fr: 'Kit premiers secours',
    en: 'First aid & medical kit'
  },
  type: {
    fr: 'Santé',
    en: 'Health'
  },
  brand: 'MediSure',
  image: 'https://images.unsplash.com/photo-1584515933487-779824d29309?auto=format&fit=crop&w=800&q=85'
}, {
  name: {
    fr: 'Batterie de cuisine',
    en: 'Professional cookware set'
  },
  type: {
    fr: 'Cuisine & table',
    en: 'Kitchen & dining'
  },
  brand: 'ChefWorks',
  image: 'https://images.unsplash.com/photo-1556911220-e15b29be8c8f?auto=format&fit=crop&w=800&q=85'
}];
export function Home() {
  useScreenInit();
  const {
    locale
  } = useLocale();
  const copy = locale === 'fr' ? {
    browse: 'Parcourir les produits',
    stats: [['85 000+ produits', 'Un choix remarquable dans tous les rayons.'], ['Livraison fiable', 'Une distribution coordonnée, partout en France.'], ['Solutions professionnelles', 'Des quantités adaptées à chaque activité.']],
    discover: 'Explorer notre univers',
    categoryTitle: 'Quelques rayons pour commencer.',
    categoryCopy: 'Découvrez une sélection ici, puis accédez à notre annuaire complet pour l’ensemble des rayons.',
    allDepartments: 'Voir tous les rayons',
    experience: 'L’expérience Centre Leclerc',
    experienceTitle: 'Un vrai hypermarché, pensé pour la vraie vie.',
    experienceCopy: 'Remplissez votre panier, parcourez des allées lumineuses et retrouvez les produits frais, la maison, le scolaire, la santé et des milliers d’indispensables en une visite.',
    fresh: 'Marché frais',
    freshDetail: 'Fruits, légumes, boulangerie, viandes et produits de la mer',
    everyday: 'Les rayons du quotidien',
    everydayDetail: 'Maison, scolaire, santé, bricolage et appareils',
    assortment: 'Découvrir l’assortiment',
    selected: 'Une sélection pour vous',
    productTitle: 'Un aperçu de notre assortiment.',
    productCopy: 'Une sélection représentative de nos gammes frais, épicerie, maison, équipement et approvisionnement.',
    available: 'Disponibilité selon magasin',
    request: 'Demander des informations',
    business: 'Gros & distribution',
    businessTitle: 'Le partenaire de votre ambition.',
    businessCopy: 'Du café indépendant aux organisations nationales, nos équipes construisent des programmes d’approvisionnement adaptés à votre activité.',
    talk: 'Parler à notre équipe professionnelle',
    trusted: 'Partenariats de confiance',
    brands: 'De grandes marques, une seule destination.',
    about: 'Bien plus qu’un hypermarché',
    aboutTitle: 'Ancré localement. Ouvert sur le monde.',
    aboutCopy: 'Société Supermarches Charentais réunit expertise retail, capacité de gros et distribution fiable, pour simplifier l’accès à des produits de qualité.',
    years: 'ans au service des produits et des personnes.',
    story: 'Découvrir notre histoire'
  } : {
    browse: 'Browse products',
    stats: [['85,000+ products', 'An exceptional choice across every department.'], ['Reliable delivery', 'Coordinated distribution throughout France.'], ['Business solutions', 'Flexible quantities for every operation.']],
    discover: 'Explore our world',
    categoryTitle: 'A few departments to start with.',
    categoryCopy: 'Browse a selected preview, then open our full directory for every department.',
    allDepartments: 'Browse all departments',
    experience: 'The Centre Leclerc experience',
    experienceTitle: 'A real hypermarket, made for real life.',
    experienceCopy: 'Fill your basket, browse bright aisles, and find fresh food, home, school, health and thousands of essentials in one visit.',
    fresh: 'Fresh food hall',
    freshDetail: 'Fruit, vegetables, bakery, meat and seafood',
    everyday: 'Everyday departments',
    everydayDetail: 'Home, school, health, DIY and appliances',
    assortment: 'Browse the assortment',
    selected: 'Selected for you',
    productTitle: 'A fuller view of our assortment.',
    productCopy: 'A representative selection from our fresh, pantry, home, equipment and supply ranges.',
    available: 'Availability varies by store',
    request: 'Request information',
    business: 'Wholesale & distribution',
    businessTitle: 'The supply partner behind your ambition.',
    businessCopy: 'From an independent café to national organisations, our specialists build supply programmes around your operation.',
    talk: 'Talk to our business team',
    trusted: 'Trusted partnerships',
    brands: 'Leading brands. One destination.',
    about: 'More than a hypermarket',
    aboutTitle: 'Locally grounded. Globally connected.',
    aboutCopy: 'Société Supermarches Charentais combines retail expertise, wholesale capability and reliable distribution to make quality products easy to access.',
    years: 'years connecting products with people.',
    story: 'Discover our story'
  };
  return <SiteLayout data-id="element-144">
      <main data-id="element-145">
        <section className="bg-[#f1f6f2] px-5 py-6 md:px-6 md:py-10" data-id="element-146">
          <div className="mx-auto grid max-w-[1320px] overflow-hidden rounded-[2rem] bg-white shadow-[0_18px_45px_rgba(14,41,35,0.12)] lg:grid-cols-[1.08fr_0.92fr]" data-id="element-147">
            <div className="relative min-h-[440px] overflow-hidden" data-id="element-148">
              <img src="https://images.unsplash.com/photo-1601598851547-4302969d0614?auto=format&fit=crop&w=1800&q=95" alt={locale === 'fr' ? 'Allées du Centre Leclerc' : 'Centre Leclerc store aisles'} className="h-full w-full object-cover" data-id="element-149" />
              <div className="absolute inset-x-0 bottom-0 bg-[#0d483d]/92 p-7 text-white md:p-10" data-id="element-150">
                <p className="text-[11px] font-extrabold uppercase tracking-[0.18em] text-[#b9e4c4]" data-id="element-151">
                  {locale === 'fr' ? 'Depuis 1969' : 'Since 1969'}
                </p>
                <h1 className="mt-3 max-w-xl text-4xl font-extrabold leading-[1.02] tracking-[-0.045em] md:text-5xl" data-id="element-152">
                  {locale === 'fr' ? 'Tout le magasin, au rythme de votre quotidien.' : 'A whole store, built around everyday life.'}
                </h1>
              </div>
            </div>
            <div className="flex flex-col justify-between p-7 md:p-10" data-id="element-153">
              <div data-id="element-154">
                <div className="flex items-center justify-between border-b border-[#d8e6dc] pb-5" data-id="element-155">
                  <p className="text-xs font-extrabold uppercase tracking-[0.16em] text-[#087c83]" data-id="element-156">
                    {locale === 'fr' ? 'Au magasin aujourd’hui' : 'In store today'}
                  </p>
                  <span className="rounded-full bg-[#e3f1e7] px-3 py-1 text-xs font-bold text-[#176443]" data-id="element-157">
                    Centre Leclerc
                  </span>
                </div>
                <p className="mt-8 text-2xl font-extrabold leading-tight text-[#12304a]" data-id="element-158">
                  {locale === 'fr' ? 'Frais, épicerie, maison, loisirs : vos essentiels réunis dans un seul lieu.' : 'Fresh food, pantry, home and leisure: your essentials together in one place.'}
                </p>
                <dl className="mt-8 grid grid-cols-2 gap-x-5 gap-y-6 border-t border-[#d8e6dc] pt-7 text-sm" data-id="element-159">
                  <div data-id="element-160">
                    <dt className="font-extrabold text-[#12304a]" data-id="element-161">
                      {locale === 'fr' ? '57 ans' : '57 years'}
                    </dt>
                    <dd className="mt-1 text-slate-500" data-id="element-162">
                      {locale === 'fr' ? 'au service de la région' : 'serving the region'}
                    </dd>
                  </div>
                  <div data-id="element-163">
                    <dt className="font-extrabold text-[#12304a]" data-id="element-164">85 000+</dt>
                    <dd className="mt-1 text-slate-500" data-id="element-165">
                      {locale === 'fr' ? 'produits à découvrir' : 'products to discover'}
                    </dd>
                  </div>
                  <div data-id="element-166">
                    <dt className="font-extrabold text-[#12304a]" data-id="element-167">
                      {locale === 'fr' ? 'Frais du jour' : 'Fresh every day'}
                    </dt>
                    <dd className="mt-1 text-slate-500" data-id="element-168">
                      {locale === 'fr' ? 'marché & boucherie' : 'market & butcher'}
                    </dd>
                  </div>
                  <div data-id="element-169">
                    <dt className="font-extrabold text-[#12304a]" data-id="element-170">
                      {locale === 'fr' ? 'Professionnels' : 'Business'}
                    </dt>
                    <dd className="mt-1 text-slate-500" data-id="element-171">
                      {locale === 'fr' ? 'devis sur demande' : 'quotation on request'}
                    </dd>
                  </div>
                </dl>
              </div>
              <Link to="/categories" className="mt-9 inline-flex w-fit items-center gap-2 rounded-full bg-[#e8432f] px-6 py-4 text-xs font-extrabold uppercase tracking-[0.1em] text-white transition hover:bg-[#c93625]" data-id="element-172">
                {locale === 'fr' ? 'Découvrir les rayons' : 'Explore departments'}{' '}
                <ArrowRightIcon size={16} data-id="element-173" />
              </Link>
            </div>
          </div>
        </section>

        <section className="border-y border-[#d8e6dc] bg-white" data-id="element-174">
          <div className="mx-auto max-w-[1320px] px-5 py-8 md:px-6" data-id="element-175">
            <div className="flex items-baseline justify-between gap-4" data-id="element-176">
              <div data-id="element-177">
                <p className="text-xs font-extrabold uppercase tracking-[0.18em] text-[#087c83]" data-id="element-178">
                  {locale === 'fr' ? 'Repérez votre rayon' : 'Find your department'}
                </p>
                <h2 className="mt-2 text-2xl font-extrabold tracking-[-0.035em] text-[#12304a]" data-id="element-179">
                  {locale === 'fr' ? 'Vos courses commencent ici.' : 'Start your shop here.'}
                </h2>
              </div>
              <Link to="/categories" className="hidden text-sm font-bold text-[#087c83] sm:inline-flex" data-id="element-180">
                {locale === 'fr' ? 'Plan du magasin →' : 'Store directory →'}
              </Link>
            </div>
            <div className="mt-7 grid grid-cols-2 gap-px overflow-hidden rounded-2xl border border-[#d8e6dc] bg-[#d8e6dc] sm:grid-cols-3 lg:grid-cols-6" data-id="element-181">
              {categories.map((category, index) => <Link to={category.to} key={category.name.fr} className="group bg-white p-4 transition hover:bg-[#eaf5ee]" data-id="element-182">
                  <span className="text-xs font-extrabold text-[#e8432f]" data-id="element-183">
                    0{index + 1}
                  </span>
                  <h3 className="mt-7 font-extrabold leading-tight text-[#12304a]" data-id="element-184">
                    {category.name[locale]}
                  </h3>
                  <p className="mt-2 text-xs leading-5 text-slate-500" data-id="element-185">
                    {category.detail[locale]}
                  </p>
                  <ArrowRightIcon size={15} className="mt-5 text-[#087c83] transition-transform group-hover:translate-x-1" data-id="element-186" />
                </Link>)}
            </div>
          </div>
        </section>

        <section className="bg-[#12304a] py-20 text-white" data-id="element-187">
          <div className="mx-auto grid max-w-[1280px] gap-10 px-5 md:grid-cols-[1.1fr_0.9fr] md:px-6" data-id="element-188">
            <div className="grid grid-cols-2 gap-3" data-id="element-189">
              <img src="https://images.unsplash.com/photo-1601598851547-4302969d0614?auto=format&fit=crop&w=1000&q=90" alt={locale === 'fr' ? 'Clients dans les allées du magasin' : 'Customers in supermarket aisles'} loading="lazy" className="col-span-2 h-64 w-full rounded-xl object-cover" data-id="element-190" />
              <img src="https://images.unsplash.com/photo-1542838132-92c53300491e?auto=format&fit=crop&w=700&q=85" alt="" loading="lazy" className="h-40 w-full rounded-xl object-cover" data-id="element-191" />
              <img src="https://images.unsplash.com/photo-1556911220-e15b29be8c8f?auto=format&fit=crop&w=700&q=85" alt="" loading="lazy" className="h-40 w-full rounded-xl object-cover" data-id="element-192" />
            </div>
            <div className="flex flex-col justify-center" data-id="element-193">
              <p className="text-xs font-bold uppercase tracking-[0.18em] text-[#7ee1df]" data-id="element-194">
                {copy.experience}
              </p>
              <h2 className="mt-4 text-4xl font-extrabold tracking-[-0.04em]" data-id="element-195">
                {copy.experienceTitle}
              </h2>
              <p className="mt-5 max-w-lg leading-7 text-slate-300" data-id="element-196">
                {copy.experienceCopy}
              </p>
              <div className="mt-7 grid grid-cols-2 gap-4 text-sm" data-id="element-197">
                <p className="border-l-2 border-[#7ee1df] pl-3" data-id="element-198">
                  <strong className="block text-white" data-id="element-199">{copy.fresh}</strong>
                  <span className="text-slate-300" data-id="element-200">{copy.freshDetail}</span>
                </p>
                <p className="border-l-2 border-[#7ee1df] pl-3" data-id="element-201">
                  <strong className="block text-white" data-id="element-202">{copy.everyday}</strong>
                  <span className="text-slate-300" data-id="element-203">{copy.everydayDetail}</span>
                </p>
              </div>
              <Link to="/categories" className="mt-8 inline-flex items-center gap-2 text-xs font-bold uppercase tracking-[0.08em] text-[#7ee1df]" data-id="element-204">
                {copy.assortment} <ArrowRightIcon size={15} data-id="element-205" />
              </Link>
            </div>
          </div>
        </section>

        <section className="bg-[#edf4f5] py-24" data-id="element-206">
          <div className="mx-auto max-w-[1440px] px-5 md:px-6" data-id="element-207">
            <SectionHeading eyebrow={copy.selected} title={copy.productTitle} description={copy.productCopy} link={copy.request} data-id="element-208" />
            <div className="mt-9 grid gap-5 sm:grid-cols-2 lg:grid-cols-4" data-id="element-209">
              {products.map(product => <article key={product.name.fr} className="overflow-hidden rounded-xl bg-white shadow-sm" data-id="element-210">
                  <div className="h-52 overflow-hidden bg-white p-6" data-id="element-211">
                    <img src={product.image} alt={`${product.name[locale]} — ${product.brand}`} loading="lazy" className="h-full w-full object-contain mix-blend-multiply" data-id="element-212" />
                  </div>
                  <div className="border-t border-slate-100 p-5" data-id="element-213">
                    <p className="text-[10px] font-bold uppercase tracking-[0.13em] text-[#087c83]" data-id="element-214">
                      {product.type[locale]}
                    </p>
                    <h3 className="mt-2 font-bold leading-snug text-[#12304a]" data-id="element-215">
                      {product.name[locale]}
                    </h3>
                    <p className="mt-1.5 text-sm text-slate-500" data-id="element-216">
                      {product.brand} · {copy.available}
                    </p>
                    <Link to="/contact" className="mt-5 inline-flex items-center gap-1.5 text-xs font-bold uppercase tracking-[0.08em] text-[#087c83]" data-id="element-217">
                      {copy.request} <ArrowRightIcon size={14} data-id="element-218" />
                    </Link>
                  </div>
                </article>)}
            </div>
          </div>
        </section>

        <section className="bg-[#12304a] py-24 text-white" data-id="element-219">
          <div className="mx-auto grid max-w-[1280px] gap-14 px-5 md:grid-cols-2 md:px-6" data-id="element-220">
            <div data-id="element-221">
              <p className="text-xs font-bold uppercase tracking-[0.18em] text-[#7ee1df]" data-id="element-222">
                {copy.business}
              </p>
              <h2 className="mt-5 max-w-lg text-4xl font-extrabold leading-tight tracking-[-0.04em] md:text-5xl" data-id="element-223">
                {copy.businessTitle}
              </h2>
              <p className="mt-6 max-w-xl leading-7 text-slate-300" data-id="element-224">
                {copy.businessCopy}
              </p>
              <Link to="/wholesale" className="mt-9 inline-flex items-center gap-2 rounded-lg bg-[#087c83] px-6 py-4 text-xs font-bold uppercase tracking-[0.09em] hover:bg-[#0a6b71]" data-id="element-225">
                {copy.talk} <ArrowRightIcon size={16} data-id="element-226" />
              </Link>
            </div>
            <div className="grid grid-cols-2 gap-x-8 gap-y-7 self-center" data-id="element-227">
              {[{
              fr: 'Volumes adaptés',
              en: 'Bulk orders'
            }, {
              fr: 'Comptes entreprise',
              en: 'Corporate supply'
            }, {
              fr: 'Hôtellerie-restauration',
              en: 'Hotel & restaurant'
            }, {
              fr: 'Collectivités',
              en: 'Institutional supply'
            }, {
              fr: 'Importation',
              en: 'Import services'
            }, {
              fr: 'Distribution',
              en: 'Distribution'
            }].map(service => <div key={service.fr} className="border-t border-white/20 pt-4" data-id="element-228">
                  <PackageCheckIcon size={20} className="mb-3 text-[#7ee1df]" data-id="element-229" />
                  <h3 className="font-bold" data-id="element-230">{service[locale]}</h3>
                </div>)}
            </div>
          </div>
        </section>

        <section className="bg-white py-24" data-id="element-231">
          <div className="mx-auto grid max-w-[1280px] items-center gap-12 px-5 md:grid-cols-2 md:px-6" data-id="element-232">
            <img src="https://images.unsplash.com/photo-1556742049-0cfed4f6a45d?auto=format&fit=crop&w=1100&q=90" alt={locale === 'fr' ? 'Équipe Société Supermarches Charentais' : 'Société Supermarches Charentais team'} className="h-[390px] w-full rounded-xl object-cover" data-id="element-233" />
            <div data-id="element-234">
              <p className="text-xs font-bold uppercase tracking-[0.18em] text-[#087c83]" data-id="element-235">
                {copy.about}
              </p>
              <h2 className="mt-4 text-4xl font-extrabold tracking-[-0.04em] text-[#12304a]" data-id="element-236">
                {copy.aboutTitle}
              </h2>
              <p className="mt-6 leading-7 text-slate-600" data-id="element-237">{copy.aboutCopy}</p>
              <div className="mt-7 flex items-end gap-4 text-[#12304a]" data-id="element-238">
                <strong className="text-5xl" data-id="element-239">57</strong>
                <span className="max-w-[180px] text-sm leading-5" data-id="element-240">
                  {copy.years}
                </span>
              </div>
              <Link to="/about" className="mt-8 inline-flex items-center gap-2 text-xs font-bold uppercase tracking-[0.08em] text-[#087c83]" data-id="element-241">
                {copy.story} <ArrowRightIcon size={15} data-id="element-242" />
              </Link>
            </div>
          </div>
        </section>
      </main>
    </SiteLayout>;
}
function SectionHeading({
  eyebrow,
  title,
  description,
  link
}: {
  eyebrow: string;
  title: string;
  description: string;
  link: string;
}) {
  return <div className="flex flex-col justify-between gap-5 md:flex-row md:items-end" data-id="element-243">
      <div data-id="element-244">
        <p className="text-xs font-bold uppercase tracking-[0.18em] text-[#087c83]" data-id="element-245">
          {eyebrow}
        </p>
        <h2 className="mt-4 text-3xl font-extrabold tracking-[-0.04em] text-[#12304a] md:text-4xl" data-id="element-246">
          {title}
        </h2>
        <p className="mt-4 max-w-xl leading-7 text-slate-600" data-id="element-247">{description}</p>
      </div>
      <Link to="/contact" className="inline-flex shrink-0 items-center gap-2 text-xs font-bold uppercase tracking-[0.08em] text-[#087c83]" data-id="element-248">
        {link}
        <ArrowRightIcon size={15} data-id="element-249" />
      </Link>
    </div>;
}